import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";

import { QRAuthToken } from "pwapi";

import { LabsState } from "shared/reducers/frontLoaderReducers";

import { fetchBarcodeBadges } from "app/actions/userActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ReactComponent as LogoSVG } from "assets/logo.svg";
import { useSelectedLab } from "hooks/useSelectedLabStation";

import CreateBadgeModal from "./CreateBadgeModal";
import EditPINModal from "./EditPINModal";

export const UserManagement = () => {
  const dispatch = useAppDispatch();
  const { state: navigationState } = useLocation();
  const lab = useSelectedLab();
  const selectedLabId = lab.id;

  const labs = useAppSelector(({ labs }: { labs: LabsState }) => labs);
  const badges = useAppSelector((sys) =>
    sys.barcodeBadges?.results?.filter(
      (b) => b.lab_id === selectedLabId && !b.deleted_at
    )
  ); // Backend should be doing this, but "just in case"

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editPIN, setEditPIN] = useState<QRAuthToken | undefined>();

  const columnHelper = createColumnHelper<QRAuthToken>();

  const columns = [
    columnHelper.accessor(
      (row) => `${row.user.first_name} ${row.user.last_name}`,
      { id: "User" }
    ),

    columnHelper.accessor(
      (row) => (row.lab_id ? labs[row.lab_id].name : null),
      { id: "Lab" }
    ),

    columnHelper.accessor(
      (row) => DateTime.fromISO(row.expires_at).toRelative(),
      {
        cell: ({ row }) => {
          return (
            <>
              {" "}
              {DateTime.fromISO(row.original.expires_at).toLocaleString()}
              <br />
              <span>
                ({DateTime.fromISO(row.original.expires_at).toRelative()})
              </span>{" "}
            </>
          );
        },
        id: "expires_at",
        header: "Expiration",
      }
    ),

    columnHelper.display({
      id: "actions",
      cell: ({ row }) => (
        <div className="Badges__actions">
          <button
            onClick={() => setEditPIN(row.original)}
            className="Button Button--blue"
          >
            Reset PIN
          </button>
          {/* <button>Remove Badge</button>
          <button>Renew Badge</button> */}
          <button
            onClick={() => {
              window.open(
                `/print_object/badge/${row.original.token}?name=${row.original.user.first_name} ${row.original.user.last_name}`
              );
            }}
            className="Button"
          >
            Print Badge
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: badges || [],
    columns,
    globalFilterFn: "includesString",

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    dispatch(fetchBarcodeBadges());
  }, []);

  if (!badges) return null;
  return (
    <div className="Cases">
      <Helmet>
        <title>Users &amp; Barcode Badges</title>
      </Helmet>
      <header className="Cases__header">
        <figure className="Logo">
          <LogoSVG />
        </figure>

        <div>
          <p>
            <strong>{lab?.name}</strong>
          </p>
          <p>User Management</p>
        </div>
      </header>
      <nav>
        <Link to={`/cases${navigationState?.search ?? ""}`}>Cases</Link>
        <Link to="/users">
          <strong>Users</strong>
        </Link>
      </nav>

      <div className="Cases__list">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: <>asc</>,
                          desc: <>desc</>,
                        }[header.column.getIsSorted() as string] ?? (
                          <svg height={0} width="1em"></svg>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`Cases__td Cases__td--${cell}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <CreateBadgeModal
          showCreateModal={showCreateModal}
          setShowCreateModal={setShowCreateModal}
        />
      )}
      {editPIN && <EditPINModal editPIN={editPIN} setEditPIN={setEditPIN} />}
    </div>
  );
};
